import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type Group = { id: string; title: string; axis_x_label: string; axis_y_label: string | null };
type NWord = { id: string; group_id: string; word: string; position_x: number; position_y: number | null; description: string };

export default function NuancePage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [nwords, setNwords] = useState<NWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [groupForm, setGroupForm] = useState<any>(null);
  const [wordForm, setWordForm] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<{type:string,id:string,label:string}|null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: gs }, { data: nws }] = await Promise.all([
      supabase.from('nuance_groups').select('*').order('title'),
      supabase.from('nuance_words').select('*').order('position_x'),
    ]);
    setGroups(gs ?? []);
    setNwords(nws ?? []);
    if (!selectedGroup && gs?.length) setSelectedGroup(gs[0].id);
    setLoading(false);
  }, [selectedGroup]);

  useEffect(() => { fetchAll(); }, [fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveGroup() {
    const { id, ...rest } = groupForm;
    if (id) await supabase.from('nuance_groups').update(rest).eq('id', id);
    else await supabase.from('nuance_groups').insert([rest]);
    setGroupForm(null); fetchAll();
  }

  async function saveWord() {
    const { id, ...rest } = wordForm;
    const payload = { ...rest, position_x: parseFloat(rest.position_x), position_y: rest.position_y ? parseFloat(rest.position_y) : null };
    if (id) await supabase.from('nuance_words').update(payload).eq('id', id);
    else await supabase.from('nuance_words').insert([payload]);
    setWordForm(null); fetchAll();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'group') {
      await supabase.from('nuance_words').delete().eq('group_id', deleteTarget.id);
      await supabase.from('nuance_groups').delete().eq('id', deleteTarget.id);
    }
    if (deleteTarget.type === 'word') await supabase.from('nuance_words').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null); fetchAll();
  }

  const currentGroup = groups.find(g => g.id === selectedGroup);
  const currentWords = nwords
    .filter(w => w.group_id === selectedGroup)
    .filter(w => !search || w.word.toLowerCase().includes(search.toLowerCase()) || w.description?.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => a.position_x - b.position_x);
  const filteredGroups = groups.filter(g => !search || g.title.toLowerCase().includes(search.toLowerCase()) || nwords.some(w => w.group_id === g.id && (w.word.toLowerCase().includes(search.toLowerCase()))));

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>↔️ Concept 3/4 — 뉘앙스 그룹</h1>
          <span className={styles.count}>{groups.length}개 그룹</span>
          <div className={styles.dbBadge}>🗄 nuance_groups · nuance_words</div>
        </div>
        <div className={styles.actions}>
          <input className={`input ${styles.search}`} placeholder="그룹/단어 검색..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-ghost" onClick={fetchAll}>↺</button>
          <button className="btn btn-ghost"
            onClick={() => setGroupForm({title:'',axis_x_label:'',axis_y_label:''})}>+ 그룹 추가</button>
          {selectedGroup && (
            <button className="btn btn-primary"
              onClick={() => setWordForm({group_id:selectedGroup,word:'',position_x:0,position_y:'',description:''})}>
              + 단어 추가
            </button>
          )}
        </div>
      </div>

      <div style={{display:'flex',gap:20}}>
        {/* Group list */}
        <div style={{width:220,flexShrink:0}}>
          <div style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.5)',letterSpacing:1.5,marginBottom:10}}>그룹</div>
          {filteredGroups.map(g => (
            <div key={g.id}
              style={{
                padding:'10px 14px', borderRadius:12, marginBottom:6, cursor:'pointer',
                background: selectedGroup===g.id ? 'rgba(232,100,154,0.22)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${selectedGroup===g.id ? 'rgba(232,100,154,0.5)' : 'rgba(255,255,255,0.12)'}`,
                display:'flex',justifyContent:'space-between',alignItems:'center',
              }}
              onClick={() => setSelectedGroup(g.id)}
            >
              <div>
                <div style={{fontSize:13,fontWeight:700}}>{g.title}</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:2}}>{g.axis_y_label ? '2D' : '1D'}</div>
              </div>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost" style={{padding:'3px 8px',fontSize:11}}
                  onClick={e => { e.stopPropagation(); setGroupForm({...g}); }}>✎</button>
                <button className="btn btn-danger" style={{padding:'3px 8px',fontSize:11}}
                  onClick={e => { e.stopPropagation(); setDeleteTarget({type:'group',id:g.id,label:g.title}); }}>✕</button>
              </div>
            </div>
          ))}
        </div>

        {/* Words */}
        <div style={{flex:1}}>
          {currentGroup && (
            <>
              <div style={{marginBottom:16,padding:'12px 16px',background:'rgba(255,255,255,0.08)',borderRadius:12,border:'1px solid rgba(255,255,255,0.12)'}}>
                <div style={{fontSize:12,color:'rgba(255,255,255,0.5)',marginBottom:4}}>X축: {currentGroup.axis_x_label}</div>
                {currentGroup.axis_y_label && <div style={{fontSize:12,color:'rgba(255,255,255,0.5)'}}>Y축: {currentGroup.axis_y_label}</div>}
              </div>
              <table className="data-table">
                <thead><tr>
                  <th>Word</th><th>Position X (-1~1)</th>
                  {currentGroup.axis_y_label && <th>Position Y (-1~1)</th>}
                  <th>Description</th><th style={{width:100}}>Actions</th>
                </tr></thead>
                <tbody>
                  {currentWords.map(w => (
                    <tr key={w.id}>
                      <td><strong>{w.word}</strong></td>
                      <td>
                        <span style={{
                          background:'rgba(155,127,212,0.2)',border:'1px solid rgba(155,127,212,0.4)',
                          borderRadius:6,padding:'2px 8px',fontSize:12,
                        }}>{w.position_x}</span>
                      </td>
                      {currentGroup.axis_y_label && <td>{w.position_y ?? '-'}</td>}
                      <td title={w.description}>{w.description}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12}}
                            onClick={() => setWordForm({...w, position_x: String(w.position_x), position_y: w.position_y != null ? String(w.position_y) : ''})}>편집</button>
                          <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:12}}
                            onClick={() => setDeleteTarget({type:'word',id:w.id,label:w.word})}>삭제</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* Group Modal */}
      {groupForm && (
        <div className="modal-overlay" onClick={() => setGroupForm(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>그룹 {groupForm.id ? '편집' : '추가'}</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setGroupForm(null)}>✕</button>
            </div>
            {[
              {key:'title',label:'Title'},
              {key:'axis_x_label',label:'X축 레이블 (예: informal ←→ formal)'},
              {key:'axis_y_label',label:'Y축 레이블 (2D만, 없으면 비워두세요)'},
            ].map(({key,label}) => (
              <div key={key} style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>{label}</label>
                <input className="input" value={groupForm[key] ?? ''} onChange={e => setGroupForm((p: any) => ({...p,[key]:e.target.value}))} />
              </div>
            ))}
            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setGroupForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={saveGroup}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* Word Modal */}
      {wordForm && (
        <div className="modal-overlay" onClick={() => setWordForm(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>단어 {wordForm.id ? '편집' : '추가'}</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setWordForm(null)}>✕</button>
            </div>
            {[
              {key:'word',label:'Word'},
              {key:'position_x',label:'Position X (-1 ~ 1)'},
              {key:'position_y',label:'Position Y (-1 ~ 1, 2D만)'},
              {key:'description',label:'Description'},
            ].map(({key,label}) => (
              <div key={key} style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>{label}</label>
                <input className="input" value={wordForm[key] ?? ''} onChange={e => setWordForm((p: any) => ({...p,[key]:e.target.value}))} />
              </div>
            ))}
            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setWordForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={saveWord}>저장</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="삭제 확인" message={`"${deleteTarget.label}"을 삭제할까요?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}
